/**
 * Datenbankzeile `public.invoices` → Rechnungsdatensatz der Fachlogik.
 *
 * Die Datenbank führt EINEN Lebenslauf-Status (draft … discarded) plus Zeitpunkte, die Fachlogik trennt
 * Prüfstatus (reviewStatus) und Zahlstatus (status). Diese Funktion ist die einzige Übersetzung; der
 * Gleichlauf-Test (tests/db/97_zahlungsdialog.test.ts) beweist, dass Datenbank-Funktionen und Fachlogik
 * aus derselben Zeile dieselben Schlüsse ziehen.
 */
import { localDate } from "./periods.js";
import { DUNNING_COLLECTION, DomainError, type Currency, type InvoiceRecord, type IsoDate, type MonthKey, type PaymentStatus, type ReviewStatus } from "./types.js";

/** Spalten von public.invoices, die die Fachlogik braucht (Zeitpunkte als ISO-Text oder Date). */
export interface InvoiceRow {
  id: string;
  org_id: string;
  kind: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";
  deal_id: string | null;
  talent_id: string | null;
  number: string | null;
  status: "draft" | "submitted" | "revision_requested" | "rejected" | "approved" | "sent" | "paid" | "cancelled" | "withdrawn" | "discarded";
  currency: string;
  gross_cents: number | string;
  issue_date: string | null;
  service_period_start: string | null;
  service_period_end: string | null;
  revenue_month: string | null;
  issuer_due_date: string | null;
  document_due_date: string | null;
  effective_due_date: string | null;
  pdf_path: string | null;
  submitted_at: Stamp;
  reviewed_at: Stamp;
  revision_reason: string | null;
  rejection_mail_sent_at: Stamp;
  sent_at: Stamp;
  sent_to_brand_count: number;
  last_sent_to_brand_at: Stamp;
  dunning_stage: string;
  dunning_blocked: boolean;
  in_collection: boolean;
  last_dunning_at: Stamp;
  dunning_deadline: string | null;
  collection_at: Stamp;
  cancelled_at: Stamp;
  paid_at: Stamp;
  payment_reference: string | null;
  talent_payment_response: "paid" | "still_overdue" | null;
  talent_payment_response_at: Stamp;
  overdue_check_requested_at: Stamp;
  admin_payment_confirmed_at: Stamp;
  discarded_at: Stamp;
  withdrawn_at: Stamp;
  credit_for?: string | null;
  paid_cents?: number | string | null;
  self_billed?: boolean | null;
  /** 0055: Zugang, Forderungsausfall, Zahlung über den Creator (fehlen in alten Datensätzen) */
  delivered_at?: Stamp;
  delivery_failed_at?: Stamp;
  bad_debt_at?: Stamp;
  paid_via_creator_at?: Stamp;
}

type Stamp = string | Date | null;

const REVIEW: Record<InvoiceRow["status"], ReviewStatus> = {
  draft: "not_submitted",
  withdrawn: "not_submitted",
  discarded: "not_submitted",
  submitted: "submitted",
  revision_requested: "revision",
  rejected: "rejected",
  approved: "approved",
  sent: "approved",
  paid: "approved",
  cancelled: "approved",
};

function day(v: string | null): IsoDate | null {
  return v ? v.slice(0, 10) : null;
}

export function invoiceFromRow(row: InvoiceRow, timeZone: string): InvoiceRecord {
  const at = (s: Stamp): IsoDate | null => (s == null ? null : localDate(s, timeZone));
  if (!(row.status in REVIEW)) throw new DomainError("INVALID_INVOICE_STATUS", String(row.status));
  // Der Status-Trigger stempelt diese Zeitpunkte; fehlen sie, ist die Zeile beschädigt
  if (row.status === "discarded" && !row.discarded_at) throw new DomainError("MISSING_STAMP", "discarded_at");
  if (row.status === "withdrawn" && !row.withdrawn_at) throw new DomainError("MISSING_STAMP", "withdrawn_at");
  const payment: PaymentStatus = row.status === "paid" ? "paid" : row.status === "cancelled" ? "cancelled" : "open";
  const revenueMonth = row.revenue_month ? (row.revenue_month.slice(0, 7) as MonthKey) : null;
  return {
    id: row.id,
    number: row.number,
    issuerId: row.kind === "agency_to_brand" || row.kind === "agency_to_talent" ? row.org_id : (row.talent_id ?? ""),
    dealId: row.deal_id,
    revenueMonth,
    reviewStatus: REVIEW[row.status],
    status: payment,
    hasPdf: !!row.pdf_path,
    amountGrossCents: Number(row.gross_cents),
    currency: row.currency as Currency,
    invoiceDate: day(row.issue_date),
    serviceDate: day(row.service_period_end ?? row.service_period_start),
    issuerDueDate: day(row.issuer_due_date),
    documentDueDate: day(row.document_due_date),
    effectiveDueDate: day(row.effective_due_date),
    submittedAt: at(row.submitted_at),
    adminReviewedAt: at(row.reviewed_at),
    rejectionReason: row.revision_reason,
    rejectionMailSentAt: at(row.rejection_mail_sent_at),
    sentToBrandAt: at(row.sent_at),
    sentToBrandCount: row.sent_to_brand_count,
    lastSentToBrandAt: at(row.last_sent_to_brand_at),
    dunningStage: row.in_collection ? DUNNING_COLLECTION : row.dunning_stage,
    lastDunningAt: at(row.last_dunning_at),
    dunningDeadline: day(row.dunning_deadline),
    dunningBlocked: row.dunning_blocked,
    collectionAt: at(row.collection_at),
    cancelledAt: at(row.cancelled_at),
    paidAt: at(row.paid_at),
    paymentProofRef: row.payment_reference,
    talentPaymentResponse: row.talent_payment_response,
    talentPaymentResponseAt: at(row.talent_payment_response_at),
    overdueCheckRequestedAt: at(row.overdue_check_requested_at),
    adminPaymentConfirmedAt: at(row.admin_payment_confirmed_at),
    discardedAt: at(row.discarded_at),
    withdrawnAt: at(row.withdrawn_at),
    // WS-17: Rechnung des Talents an die eigene Agentur
    recipientIsOwnOrg: row.kind === "talent_to_agency",
    kind: row.kind,
    creditFor: row.credit_for ?? null,
    paidCents: Number(row.paid_cents ?? 0),
    selfBilled: !!row.self_billed,
    // undefined, wenn die Spalte fehlt (alte Datensätze): gilt als zugegangen
    ...(row.delivered_at !== undefined ? { deliveredAt: at(row.delivered_at) } : {}),
    ...(row.delivery_failed_at !== undefined ? { deliveryFailedAt: at(row.delivery_failed_at) } : {}),
    ...(row.bad_debt_at !== undefined ? { badDebtAt: at(row.bad_debt_at) } : {}),
    ...(row.paid_via_creator_at !== undefined ? { paidViaCreatorAt: at(row.paid_via_creator_at) } : {}),
  };
}
